import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { formatPrice } from '../../../utils/formatters';
import { supabase } from '../../../lib/supabase';

const formatSeoPath = (name, categoryName) => {
    if (!name) return '';
    const safeCat = (categoryName || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    let prefix = `/product`;
    if (['automotive', 'motorcycles'].includes(safeCat)) prefix = '/vehicle';
    
    let processedName = name.toLowerCase();
    let year = '';
    if (prefix === '/vehicle') {
      const yearMatch = processedName.match(/\b(19\d{2}|20\d{2})\b/);
      if (yearMatch) {
        year = `/${yearMatch[1]}`;
        processedName = processedName.replace(yearMatch[0], '').trim();
      }
    }
    
    const pathSegments = processedName.replace(/[^a-z0-9\s.-]/g, '').trim().split(/\s+/).filter(Boolean).join('/');
    return `${prefix}${year}/${pathSegments}`;
};

const ComponentCard = ({ component, onAddToCart, onDirectAddToCart, isAuthenticated, onAuthRequired, onViewDetails, onAddToWishlist, onRemoveFromCollection, collectionItems, isStorefront = false, cardLayout = null }) => {
  const navigate = useNavigate();

  const layoutConfig = cardLayout?.layouts?.componentCatalog;

  const handleAddToCartClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (onAddToCart) onAddToCart(component, true);
  };

  const handleDirectAddToCartClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (onDirectAddToCart) onDirectAddToCart(component, true);
  };

  const renderElement = (id) => {
    const conf = layoutConfig?.elements?.[id] || {};
    const widthClass = conf.width === 'half' ? 'w-[48%]' : conf.width === 'auto' ? 'w-auto' : 'w-full';
    
    let marginClass = '';
    if (conf.align === 'center') marginClass = 'mx-auto';
    else if (conf.align === 'right') marginClass = 'ml-auto';
    else if (conf.align === 'left') marginClass = 'mr-auto';

    const alignClass = conf.align === 'center' ? 'text-center justify-center' : conf.align === 'right' ? 'text-right justify-end ml-auto' : 'text-left justify-start';
    const flexAlignClass = conf.align === 'center' ? 'justify-center' : conf.align === 'right' ? 'justify-end ml-auto' : 'justify-start';

    switch (id) {
      case 'title':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-sm font-semibold text-foreground hover:text-primary transition-colors mb-1 break-words leading-tight`}>
            {component?.customName || component?.name}
          </div>
        );
      case 'manufacturer':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-[10px] text-muted-foreground font-normal uppercase tracking-wider mb-1`}>
            Mfg: {(component?.is3dManufacturing || component?.isCncCapable || component?.manufacturer?.name === 'SELF' || component?.manufacturerName === 'SELF') ? (component?.storeName || component?.seller?.business_name || 'Direct Seller') : (component?.manufacturer?.name || component?.manufacturerName || 'OpenGadget')}
          </div>
        );
      case 'parent_model':
        return component?.product?.name && (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-xs text-muted-foreground italic mb-1`}>
            for {component.product.name}
          </div>
        );
      case 'store_name':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-xs text-muted-foreground font-medium mb-1 flex items-center gap-1`}>
            <Icon name="Store" size={12} className="text-muted-foreground/60" />
            <span>{component?.storeName || 'OpenGadget'}</span>
          </div>
        );
      case 'description':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-xs text-muted-foreground mb-2 line-clamp-2`}>
            {component?.description}
          </div>
        );
      case 'price':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-sm font-bold text-primary mb-1`}>
            {formatPrice(component?.price)}
          </div>
        );
      case 'rating':
        if (conf.ratingStyle === 'star1') {
          return (
            <div 
              key={id}
              onClick={(e) => { e.stopPropagation(); navigate(`/product-reviews-ratings?productId=${component.productId || component.product?.id}&componentId=${component.id}`); }}
              className={`${widthClass} ${marginClass} flex items-center ${flexAlignClass} space-x-1 my-1 cursor-pointer hover:opacity-85 transition-opacity text-xs text-foreground font-semibold`}
            >
              <Icon name="Star" size={12} className="text-yellow-400 fill-current" />
              <span className="text-sm font-semibold text-gray-800">{component?.averageRating?.toFixed(1) || '0.0'}</span>
              <span className="text-xs text-muted-foreground font-normal">({component?.totalRatings || 0})</span>
            </div>
          );
        }
        return (
          <div 
            key={id}
            onClick={(e) => { e.stopPropagation(); navigate(`/product-reviews-ratings?productId=${component.productId || component.product?.id}&componentId=${component.id}`); }}
            className={`${widthClass} ${marginClass} flex items-center ${flexAlignClass} space-x-1 my-1 cursor-pointer hover:opacity-85 transition-opacity`}
          >
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Icon 
                  key={star} 
                  name="Star" 
                  size={10} 
                  className={`${star <= Math.round(component?.averageRating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            <span className="text-[10px] text-muted-foreground">({component?.totalRatings || 0})</span>
          </div>
        );
      case 'component_count':
        return null; // Not applicable for components
      case 'views_count':
        return (
          <div key={id} className={`${widthClass} ${marginClass} flex ${flexAlignClass} mb-1`}>
            <div className="text-[9px] text-muted-foreground font-bold bg-muted px-2 py-0.5 rounded border border-border inline-block shadow-sm whitespace-nowrap">
              {(component?.viewsCount || 0).toLocaleString()} VIEWS
            </div>
          </div>
        );
      case 'action_button':
        return (
          <div key={id} className={`${widthClass} ${marginClass} flex ${flexAlignClass} mb-1`}>
            <Button
              variant="default"
              size="sm"
              onClick={handleAddToCartClick}
              className="w-full text-xs py-1.5 h-auto"
            >
              Add to Cart
            </Button>
          </div>
        );
      case 'skinny_view_3d':
        return (
          <div key={id} className={`${widthClass} ${marginClass} mb-1`}>
            <Button
              variant="secondary"
              size="xs"
              iconName="Box"
              iconPosition="left"
              onClick={(e) => { e.stopPropagation(); handleViewIn3D(component); }}
              className="w-full text-[10px] py-1.5 h-auto flex items-center justify-center"
              style={conf.bg ? { backgroundColor: conf.bg, borderColor: conf.bg, color: '#ffffff' } : undefined}
            >
              {conf.text || "View 3D"}
            </Button>
          </div>
        );
      case 'skinny_add_to_cart':
        return (
          <div key={id} className={`${widthClass} ${marginClass} mb-1`}>
            <Button
              variant="default"
              size="xs"
              iconName="ShoppingCart"
              iconPosition="left"
              onClick={handleAddToCartClick}
              className="w-full text-[10px] py-1.5 h-auto flex items-center justify-center"
              style={conf.bg ? { backgroundColor: conf.bg, borderColor: conf.bg, color: '#ffffff' } : undefined}
            >
              {conf.text || "Add to Cart"}
            </Button>
          </div>
        );
      case 'skinny_quick_add':
        return !isStorefront && component?.activeSellerCount > 0 && (
          <div key={id} className={`${widthClass} ${marginClass} mb-1`}>
            <Button
              variant="success"
              size="xs"
              iconName="Zap"
              iconPosition="left"
              onClick={handleDirectAddToCartClick}
              className="w-full text-[10px] py-1.5 h-auto flex items-center justify-center bg-green-600 hover:bg-green-700"
              style={conf.bg ? { backgroundColor: conf.bg, borderColor: conf.bg, color: '#ffffff' } : undefined}
            >
              {conf.text || "Quick Add"}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderOverlayElement = (id) => {
    const conf = layoutConfig?.elements?.[id] || {};

    switch (id) {
      case 'component_count':
        return renderElement('component_count');
      case 'featured':
        return component?.staffpick && (
          <span key={id} style={{ backgroundColor: conf.bg }} className="px-2 py-0.5 bg-purple-500 text-white text-[10px] font-bold rounded-md shadow-sm">
            {conf.text || "STAFF PICK"}
          </span>
        );
      case 'favorite':
        return (
          <button
            key={id}
            onClick={(e) => handleFavoriteClick(e, component)}
            className={`p-1.5 rounded-full transition-all duration-200 ${isSaved ? 'bg-red-500/10' : 'bg-background/80 backdrop-blur-sm hover:bg-background'}`}
          >
            <Icon name="Heart" size={14} className={isSaved ? 'fill-red-500 text-red-500' : 'text-muted-foreground'} />
          </button>
        );
      case 'mfg_badge':
        return (component?.is3dManufacturing || component?.isCncCapable) && (
          <span key={id} style={{ backgroundColor: conf.bg }} className="px-2 py-0.5 text-white text-[10px] font-bold rounded-md shadow-sm flex items-center gap-1">
            <Icon name="Zap" size={10} className="fill-current" />
            {component?.is3dManufacturing ? (conf.text || '3D') : 'CNC'}
          </span>
        );
      default:
        return null;
    }
  };

  const handleViewIn3D = (component) => {
    // Symmetrical Component Views Increments:
    // Increments views on commercial seller offering and parent global components dynamically!
    if (component?.id && component.id.length === 36 && component.id.includes('-')) {
      supabase?.rpc('increment_seller_component_views', { seller_component_id: component.id })?.then(undefined, err => {
        console.error('Error incrementing seller component views:', err);
      });
    }
    const globalCompId = component.dbComponentId || component.componentId || component.component_id || component.component?.id;
    if (globalCompId) {
      supabase?.rpc('increment_component_views', { component_id: globalCompId })?.then(undefined, err => {
        console.error('Error incrementing global component views:', err);
      });
    }

    const pId = component.productId || component.product?.id;
    if (pId) {
      const pName = component.product?.name;
      const cSlug = component.slug;
      
      let url = `/3d-model-viewer?id=${pId}&componentId=${component.id}`;
      
      if (pName) {
        url = formatSeoPath(pName, component.product?.categoryName || component.product?.category?.name);
        if (cSlug) {
            url += `/${cSlug}`;
        }
        url += `/parts-diagram?componentId=${component.id}&id=${pId}`;
      }
      
      url += (url.includes('?') ? '&' : '?') + `viewMode=x-ray-gray&isPlaying=false`;
      navigate(url);
    } else {
      alert("This component is not linked to a 3D model.");
    }
  };

  const handleFavoriteClick = async (e, component) => {
    e.stopPropagation();
    if (!isAuthenticated) {
        onAuthRequired();
        return;
    }
    const isSaved = collectionItems?.includes(component.id);
    if (isSaved) {
        await onRemoveFromCollection(component.id);
    } else {
        await onAddToWishlist(component.id);
    }
  };

  const handleViewRatings = (e, component) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      onAuthRequired('Please log in to view ratings and reviews.');
      return;
    }
    const pId = component.productId || component.product?.id;
    if (pId) {
      navigate(`/product-reviews-ratings?productId=${pId}&componentId=${component.id}`);
    } else {
      alert("This component is not linked to a 3D model with reviews.");
    }
  };

  const isSaved = collectionItems?.includes(component?.id);

  if (layoutConfig) {
    return (
      <div 
        className="bg-card border border-border rounded-lg p-4 flex flex-col hover:shadow-lg transition-shadow group relative cursor-pointer"
        onClick={() => {
          // Symmetrical Component Views Increments:
          // Increments views on commercial seller offering and parent global components dynamically!
          if (component?.id && component.id.length === 36 && component.id.includes('-')) {
            supabase?.rpc('increment_seller_component_views', { seller_component_id: component.id })?.then(undefined, err => {
              console.error('Error incrementing seller component views:', err);
            });
          }
          const globalCompId = component.dbComponentId || component.componentId || component.component_id || component.component?.id;
          if (globalCompId) {
            supabase?.rpc('increment_component_views', { component_id: globalCompId })?.then(undefined, err => {
              console.error('Error incrementing global component views:', err);
            });
          }
          
          if (onViewDetails) onViewDetails(component);
        }}
      >
        {/* Image wrapper with overlays */}
        <div className="relative w-full h-48 bg-muted rounded-md overflow-hidden flex items-center justify-center mb-4 shrink-0">
          <Image 
            src={component?.images?.[0] || component?.productThumbnail || "/assets/images/no_image.png"} 
            alt={component?.customName || component?.name} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
            {layoutConfig.imageOverlays?.topLeft?.map(id => renderOverlayElement(id))}
          </div>
          <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
            {layoutConfig.imageOverlays?.topRight?.map(id => renderOverlayElement(id))}
          </div>
          <div className="absolute bottom-2 left-2 flex flex-col gap-1 z-10">
            {layoutConfig.imageOverlays?.bottomLeft?.map(id => renderOverlayElement(id))}
          </div>
          <div className="absolute bottom-2 right-2 flex flex-col gap-1 z-10">
            {layoutConfig.imageOverlays?.bottomRight?.map(id => renderOverlayElement(id))}
          </div>
        </div>

        {/* Dynamic Content Stack */}
        <div className="flex-grow flex flex-row flex-wrap items-center content-start gap-y-2 gap-x-2 w-full">
          {layoutConfig.contentStack?.map(id => renderElement(id))}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-card border border-border rounded-lg p-4 flex flex-col hover:shadow-lg transition-shadow group relative cursor-pointer"
      onClick={() => {
        // Symmetrical Component Views Increments:
        // Increments views on commercial seller offering and parent global components dynamically!
        if (component?.id && component.id.length === 36 && component.id.includes('-')) {
          supabase?.rpc('increment_seller_component_views', { seller_component_id: component.id })?.then(undefined, err => {
            console.error('Error incrementing seller component views:', err);
          });
        }
        const globalCompId = component.dbComponentId || component.componentId || component.component_id || component.component?.id;
        if (globalCompId) {
          supabase?.rpc('increment_component_views', { component_id: globalCompId })?.then(undefined, err => {
            console.error('Error incrementing global component views:', err);
          });
        }
        
        if (onViewDetails) onViewDetails(component);
      }}
    >
      <div className="relative w-full h-48 bg-muted rounded-md overflow-hidden flex items-center justify-center mb-4 shrink-0">
        <Image 
          src={component?.images?.[0] || component?.productThumbnail || "/assets/images/no_image.png"} 
          alt={component?.customName || component?.name} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />

        <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
          {(component?.globalGenuinePart || component?.genuinePart) && (
            <span className="px-2 py-0.5 bg-blue-700 text-white text-[10px] font-bold rounded-full shadow-sm">
              GENUINE
            </span>
          )}
          {(component?.globalStaffpick) && (
            <span className="px-2 py-0.5 bg-purple-500 text-white text-[10px] font-bold rounded-md shadow-sm">
              STAFF PICK
            </span>
          )}
          {(component?.is3dManufacturing || component?.isCncCapable) && (
            <span className="px-2 py-0.5 bg-orange-600 text-white text-[10px] font-bold rounded-md shadow-sm flex items-center gap-1">
              <Icon name="Zap" size={10} className="fill-current" />
              3D
            </span>
          )}
          {isStorefront && component?.staffpick && (

            <span className="px-2 py-0.5 bg-purple-400 text-white text-[10px] font-bold rounded-md shadow-sm">
              SELLER PICK
            </span>
          )}
        </div>
        
          <button
          onClick={(e) => handleFavoriteClick(e, component)}
          className={`
            absolute top-2 right-2 p-1.5 rounded-full z-10 transition-all duration-200
            ${isSaved ? 'bg-red-50 text-red-500' : 'bg-black/50 text-white hover:bg-black/70'}
          `}
          title={isSaved ? "Remove from Favorites" : "Add to Favorites"}
        >
          <Icon name="Heart" size={16} className={isSaved ? 'fill-current' : ''} />
        </button>
      </div>

      <div className="flex-grow flex flex-col min-w-0">
                <h3 className="text-sm font-semibold text-foreground line-clamp-2 min-h-[2.5rem] mb-1">
          {component?.customName || component?.name}
        </h3>
        
        <div className="flex-grow">
          {component?.product?.name && (
            <span className="text-muted-foreground font-normal text-xs block mt-0.5">
                for {component.product.name}
            </span>
          )}
          {(component?.seasonalSetting || component?.holidaySetting) && (
            <div className="flex gap-1.5 mt-1 flex-wrap">
              {component?.seasonalSetting && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-500/10 text-green-700">
                  🍂 {component.seasonalSetting}
                </span>
              )}
              {component?.holidaySetting && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500/10 text-red-700">
                  🎉 {component.holidaySetting}
                </span>
              )}
            </div>
          )}
          {(component?.manufacturer || component?.manufacturerName || component?.is3dManufacturing || component?.isCncCapable) && (
            <span className="text-muted-foreground font-normal text-[10px] block uppercase mt-0.5 tracking-wider">
                Mfg: {(component?.is3dManufacturing || component?.isCncCapable || component?.manufacturer?.name === 'SELF' || component?.manufacturerName === 'SELF') ? (component?.storeName || component?.seller?.business_name || 'Direct Seller') : (component?.manufacturer?.name || component?.manufacturerName)}
            </span>
          )}
        </div>
        
        <div 
          className="flex items-center space-x-1 my-1 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={(e) => handleViewRatings(e, component)}
          title={!isAuthenticated ? 'Log in to view reviews' : 'View all ratings and reviews'}
        >
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Icon 
                key={star} 
                name="Star" 
                size={12} 
                className={`${star <= Math.round(component?.averageRating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
              />
            ))}
          </div>
          <span className="text-[10px] text-muted-foreground">({component?.totalRatings || 0})</span>
          <span className="text-[10px] text-muted-foreground flex items-center ml-2 border-l border-border pl-2" title="Views Count">
            <Icon name="Eye" size={12} className="mr-1" />
            {(component?.viewsCount || 0).toLocaleString()}
          </span>
        </div>

        <div className="mt-auto pt-2 border-t border-border/50">
          <div className="flex justify-between items-center mb-2">
            <span className="text-base font-bold text-primary">{formatPrice(component?.price)}</span>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground truncate max-w-[100px]">
              <Icon name="Store" size={10} />
              {component?.storeSlug ? (
                <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(`/store/${component.storeSlug}`); }} className="hover:text-primary transition-colors truncate">
                    {component?.storeName || 'Store'}
                  </button>
              ) : (
                <span className="truncate">{component?.storeName || 'OpenGadget'}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 mt-auto shrink-0">
        <Button
          variant="secondary"
          size="sm"
          onClick={(e) => { e.stopPropagation(); handleViewIn3D(component); }}
          title="View in 3D Model"
          className="w-full text-xs py-1 h-auto"
        >
          <Icon name="Box" size={14} className="mr-1" />
          View 3D
        </Button>

        <Button
          variant="default"
          size="sm"
          iconName={!isStorefront ? "Store" : "ShoppingCart"}
          onClick={(e) => { 
            e.stopPropagation(); 
            if (isStorefront && (component.is3dManufacturing || component.isCncCapable)) {
               onViewDetails(component);
            } else {
               onAddToCart(component, true); 
            }
          }}
          className="w-full text-xs py-1.5 h-auto"
        >
          {!isStorefront ? 'Add to Cart' : 'Add to Cart'}
        </Button>
        
        {(!isStorefront && component?.activeSellerCount > 0 && onDirectAddToCart) && (
          <Button
            variant="default"
            size="sm"
            iconName="ShoppingCart"
            onClick={(e) => { 
                e.stopPropagation(); 
                if (component.is3dManufacturing || component.isCncCapable) {
                    onViewDetails(component);
                } else {
                    onDirectAddToCart(component, true); 
                }
            }}
            className="w-full text-xs py-1.5 h-auto bg-green-600 hover:bg-green-700"
          >
            Quick Add
          </Button>
        )}
      </div>
    </div>
  );
};

export default ComponentCard;
